import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
// import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  VocabularyTermPicker,
  type VocabularyTermValue,
} from '@/components/vocabulary/VocabularyTermPicker';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useFeatureMutations } from '@/features/patient-record/hooks/useFeatureMutations';
import {
  toDraft,
  toInput,
  type DraftState,
} from '@/features/patient-record/lib/featureForm';
import { MODIFIER_SPECS } from '@/features/patient-record/lib/featureModifiers';
import type { FeatureViewModel } from '@/features/patient-record/types';

import { FormField } from '../_shared/FormField';

/**
 * HPO subtree roots the onset / severity pickers scope to, so each binds to
 * a real modifier term rather than free text. Both branches carry no
 * `term_category`, so the pickers materialise the subtree via `descendantsOf`
 * (see PTV2-1168) rather than the category filter. The four additional
 * modifier subtrees live in {@link MODIFIER_SPECS}.
 */
const ONSET_SUBTREE_ROOT = 'HP:0003674'; // "Onset"
const SEVERITY_SUBTREE_ROOT = 'HP:0012824'; // "Severity"

interface FeatureRowEditorProps {
  readonly guid: string;
  readonly feature: FeatureViewModel;
  readonly onClose: () => void;
}

/**
 * Modal editor for a phenotype's qualifier fields (date / onset / severity /
 * modifiers / occurrence / notes). The row header handles status (cycle chip),
 * label / HPO id (read-only), and delete. Commits on the explicit Save button;
 * the dirty guard raises Save / Discard / Keep editing on a dirty dismiss.
 */
export function FeatureRowEditor({
  guid,
  feature,
  onClose,
}: FeatureRowEditorProps): ReactElement {
  const { t } = useTranslation();
  const { update } = useFeatureMutations(guid);
  const label = t('patient.phenotypes.editorLabel', { defaultValue: 'Phenotype' });

  const form = useEditableDraft<DraftState>({
    initial: toDraft(feature),
    label,
    autosave: false,
    onSave: (d) => update.mutateAsync({ id: feature.id, input: toInput(feature, d) }),
  });
  const { draft } = form;

  const saveAndClose = (): void => {
    update.mutate({ id: feature.id, input: toInput(feature, draft) }, { onSuccess: onClose });
  };

  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving: update.isPending,
    label,
    onSave: saveAndClose,
    onClose,
    register: false,
  });

  const onsetValue: VocabularyTermValue | null =
    draft.onsetLabel.length > 0
      ? { id: draft.onsetTermId, label: draft.onsetLabel }
      : null;
  const severityValue: VocabularyTermValue | null =
    draft.severityLabel.length > 0
      ? { id: draft.severityTermId, label: draft.severityLabel }
      : null;

  return (
    <>
      <Dialog open {...guard.rootProps}>
        <DialogContent
          className="sm:max-w-5xl max-h-[calc(100vh-3rem)] p-0 gap-0 flex flex-col"
          {...guard.contentProps}
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle className="flex items-baseline gap-2">
              <span className="truncate">{feature.label}</span>
              {feature.hpoId.length > 0 && (
                <span className="shrink-0 text-[11px] font-mono font-normal text-muted-foreground">
                  {feature.hpoId}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div
            ref={form.containerProps.ref}
            className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <FormField label={t('patient.phenotypes.fields.dateObserved')}>
          <DateInput
            value={draft.dateObserved}
            onChange={(value) => {
              form.setDraft((d) => ({ ...d, dateObserved: value }));
            }}
          />
        </FormField>
        <FormField label={t('patient.phenotypes.fields.onsetLabel')}>
          <VocabularyTermPicker
            vocabularyId="hpo"
            descendantsOf={ONSET_SUBTREE_ROOT}
            value={onsetValue}
            onChange={(next) => {
              form.setDraft((d) => ({
                ...d,
                onsetLabel: next?.label ?? '',
                onsetTermId: next?.id ?? '',
              }));
            }}
            placeholder={t('patient.phenotypes.fields.onsetPlaceholder')}
            ariaLabel={t('patient.phenotypes.fields.onsetLabel')}
          />
        </FormField>
        <FormField label={t('patient.phenotypes.fields.severityLabel')}>
          <VocabularyTermPicker
            vocabularyId="hpo"
            descendantsOf={SEVERITY_SUBTREE_ROOT}
            value={severityValue}
            onChange={(next) => {
              form.setDraft((d) => ({
                ...d,
                severityLabel: next?.label ?? '',
                severityTermId: next?.id ?? '',
              }));
            }}
            placeholder={t('patient.phenotypes.fields.severityPlaceholder')}
            ariaLabel={t('patient.phenotypes.fields.severityLabel')}
          />
        </FormField>
        {MODIFIER_SPECS.map((spec) => {
          const current = draft.modifiers[spec.key];
          const value: VocabularyTermValue | null =
            current.label.length > 0
              ? { id: current.termId, label: current.label }
              : null;
          return (
            <FormField
              key={spec.key}
              label={t(`patient.phenotypes.fields.${spec.key}Label`)}
            >
              <VocabularyTermPicker
                vocabularyId="hpo"
                descendantsOf={spec.subtreeRootId}
                value={value}
                onChange={(next) => {
                  form.setDraft((d) => ({
                    ...d,
                    modifiers: {
                      ...d.modifiers,
                      [spec.key]: {
                        label: next?.label ?? '',
                        termId: next?.id ?? '',
                      },
                    },
                  }));
                }}
                placeholder={t(`patient.phenotypes.fields.${spec.key}Placeholder`)}
                ariaLabel={t(`patient.phenotypes.fields.${spec.key}Label`)}
              />
            </FormField>
          );
        })}
      </div>
      {/* Occurrence hidden until the BE supports an OCCURRENCE qualifier (PTV2-1547).
      <FormField label={t('patient.phenotypes.fields.occurrenceLabel')}>
        <Select
          value={draft.occurrence}
          onChange={(event) => {
            form.setDraft((d) => ({ ...d, occurrence: event.target.value }));
          }}
          className="h-10 w-full text-sm"
          aria-label={t('patient.phenotypes.fields.occurrenceLabel')}
        >
          <option value="">
            {t('patient.phenotypes.fields.occurrenceUnset')}
          </option>
          <option value="PRE_APPOINTMENT">
            {t('patient.phenotypes.occurrence.preAppointment')}
          </option>
          <option value="POST_APPOINTMENT">
            {t('patient.phenotypes.occurrence.postAppointment')}
          </option>
          <option value="BOTH">
            {t('patient.phenotypes.occurrence.both')}
          </option>
        </Select>
      </FormField>
      */}
            <FormField label={t('patient.phenotypes.fields.notesLabel')}>
              <Textarea
                value={draft.notes}
                placeholder={t('patient.phenotypes.fields.notesPlaceholder')}
                rows={3}
                onChange={(event) => {
                  form.setDraft((d) => ({ ...d, notes: event.target.value }));
                }}
              />
            </FormField>
          </div>
          <DialogFooter className="shrink-0 border-t border-border px-6 py-3">
            <Button
              type="button"
              variant="ghost"
              onClick={guard.requestClose}
              disabled={update.isPending}
            >
              {t('ui.dialog.close')}
            </Button>
            <Button
              type="button"
              onClick={saveAndClose}
              loading={update.isPending}
              loadingText={t('patient.dirtyFooter.saving')}
            >
              {t('patient.phenotypes.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.confirm}
    </>
  );
}
